import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthProvider'

const ACTIVE_ROLE_KEY = 'active_role'
const ROLE_ADMIN = 'admin'
const ROLE_USER = 'user'

/**
 * Custom hook for managing user role switching
 * Handles role state, persistence, and navigation
 */
export function useRole() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [activeRole, setActiveRole] = useState(() => {
    // Initialize from localStorage or default based on user type
    const storedRole = localStorage.getItem(ACTIVE_ROLE_KEY)
    if (storedRole === ROLE_ADMIN || storedRole === ROLE_USER) {
      return storedRole
    }
    // Default to admin for admin users, user for normal users
    return user?.is_admin ? ROLE_ADMIN : ROLE_USER
  })

  // Sync activeRole with user changes (e.g., after login)
  useEffect(() => {
    if (user) {
      const storedRole = localStorage.getItem(ACTIVE_ROLE_KEY)
      if (!storedRole) {
        // Set default role if not already set
        const defaultRole = user.is_admin ? ROLE_ADMIN : ROLE_USER
        setActiveRole(defaultRole)
        localStorage.setItem(ACTIVE_ROLE_KEY, defaultRole)
      }
    }
  }, [user])

  /**
   * Switch between admin and user roles
   * @param {string} newRole - 'admin' or 'user'
   */
  const switchRole = useCallback(
    (newRole) => {
      if (!user?.is_admin) {
        console.warn('Only admin users can switch roles')
        return
      }

      if (newRole !== ROLE_ADMIN && newRole !== ROLE_USER) {
        console.error('Invalid role:', newRole)
        return
      }

      // Update state and localStorage
      setActiveRole(newRole)
      localStorage.setItem(ACTIVE_ROLE_KEY, newRole)

      // Navigate to appropriate route
      if (newRole === ROLE_USER) {
        navigate('/chat')
      } else if (newRole === ROLE_ADMIN) {
        const adminRedirectUrl =
          process.env.REACT_APP_ADMIN_REDIRECT_URL_AFTER_LOGIN || '/admin/users'
        navigate(adminRedirectUrl)
      }
    },
    [user, navigate]
  )

  /**
   * Reset role to default (admin for admin users)
   * Called on logout
   */
  const resetRole = useCallback(() => {
    if (user?.is_admin) {
      setActiveRole(ROLE_ADMIN)
      localStorage.setItem(ACTIVE_ROLE_KEY, ROLE_ADMIN)
    } else {
      setActiveRole(ROLE_USER)
      localStorage.setItem(ACTIVE_ROLE_KEY, ROLE_USER)
    }
  }, [user])

  return {
    activeRole,
    switchRole,
    resetRole,
    isAdmin: user?.is_admin || false,
    ROLE_ADMIN,
    ROLE_USER,
  }
}

